import { Monitor, Smartphone, Tablet, CheckCircle, XCircle } from 'lucide-react'
import type { PendingDevice } from '@portal/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface Props {
  devices: PendingDevice[]
  onApprove: (id: string) => void
  onReject: (id: string) => void
}

function DeviceIcon({ type }: { type: string }) {
  if (type === 'MOBILE') return <Smartphone className="h-4 w-4 text-muted-foreground" />
  if (type === 'TABLET') return <Tablet className="h-4 w-4 text-muted-foreground" />
  return <Monitor className="h-4 w-4 text-muted-foreground" />
}

export default function PendingDeviceList({ devices, onApprove, onReject }: Props) {
  if (devices.length === 0) return null

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold">
            {devices.length}
          </span>
          Device Approval Requests
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-amber-200 bg-amber-100/50">
                <th className="px-4 py-2.5 text-left font-medium text-amber-800">User</th>
                <th className="px-4 py-2.5 text-left font-medium text-amber-800">Device</th>
                <th className="px-4 py-2.5 text-left font-medium text-amber-800">IP Address</th>
                <th className="px-4 py-2.5 text-left font-medium text-amber-800">Requested</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id} className="border-b border-amber-100 last:border-0 hover:bg-amber-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{device.user.name}</p>
                    <p className="text-xs text-muted-foreground">{device.user.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <DeviceIcon type={device.deviceType} />
                      <span className="text-foreground">{device.deviceName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                    {device.ipAddress ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {new Date(device.createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                        onClick={() => onApprove(device.id)}
                      >
                        <CheckCircle className="mr-1 h-3.5 w-3.5" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                        onClick={() => onReject(device.id)}
                      >
                        <XCircle className="mr-1 h-3.5 w-3.5" />
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
